import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "../supabase/supabase-client";
import { Json } from "../types/supabase";

// The kart game's races (see the kart_runs migration): each one saved as
// it ends — only if it was driven without help, and not in training — and each
// member's best score and time per track and difficulty read back (the
// kart_records view) as the records to beat. Neither is allowed to get in the way of playing — if
// the table isn't there, or the network isn't, the game goes on without.

export interface KartRun {
  memberId: number;
  track: string;
  difficulty: string;
  score: number;
  stars: number;
  place: number;
  raceTime: number | null;
  abandoned: boolean;
  summary: Json;
}

/** A member's best on a track and difficulty, driven without help: their highest score and their fastest time. */
export interface KartRecord {
  memberId: number;
  track: string;
  difficulty: string;
  score: number;
  time: number | null;
}

export const useKartRecords = () =>
  useQuery({
    queryKey: ["kart", "records"],
    queryFn: async (): Promise<KartRecord[]> => {
      const { data, error } = await supabase.from("kart_records").select("member_id, track, difficulty, score, race_time");
      if (error) throw error;
      return data.map((b) => ({ memberId: b.member_id!, track: b.track!, difficulty: b.difficulty!, score: b.score!, time: b.race_time }));
    },
    retry: false,
  });

export const useSaveKartRun = () => {
  const queryClient = useQueryClient();
  return useMutation(
    async (run: KartRun) => {
      const { error } = await supabase.from("kart_runs").insert({
        member_id: run.memberId,
        track: run.track,
        difficulty: run.difficulty,
        score: run.score,
        stars: run.stars,
        place: run.place,
        race_time: run.raceTime,
        abandoned: run.abandoned,
        summary: run.summary,
      });
      if (error) throw error;
    },
    { onSuccess: () => queryClient.invalidateQueries(["kart"]) },
  );
};

/** A recorded run to race against: whose, how fast, and its line (see GHOST_STEP in the kart renderer). */
export interface KartGhost {
  memberId: number;
  time: number;
  path: number[];
}

/** The fastest saved run on a track that carries its line, or null — never an error: no ghost is fine. */
export async function fetchKartGhost(track: string): Promise<KartGhost | null> {
  const { data, error } = await supabase
    .from("kart_runs")
    .select("member_id, race_time, ghost:summary->ghost")
    .eq("track", track)
    .eq("abandoned", false)
    .eq("summary->assists->>steer", "false")
    .eq("summary->assists->>drift", "false")
    .eq("summary->assists->>item", "false")
    .not("summary->ghost", "is", null)
    .order("race_time", { ascending: true })
    .limit(1);
  const row = data?.[0];
  if (error || !row || row.race_time === null || !Array.isArray(row.ghost)) return null;
  return { memberId: row.member_id, time: row.race_time, path: row.ghost as number[] };
}

/** This week's runs on the weekly challenge: each member's best, by its goal. */
export const useKartWeek = (track: string, difficulty: string, since: Date, goal: "score" | "time") =>
  useQuery({
    queryKey: ["kart", "week", track, difficulty, since.toISOString(), goal],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("kart_runs")
        .select("member_id, score, race_time")
        .eq("track", track)
        .eq("difficulty", difficulty)
        .eq("abandoned", false)
        .eq("summary->assists->>steer", "false")
        .eq("summary->assists->>drift", "false")
        .eq("summary->assists->>item", "false")
        .gte("created_at", since.toISOString());
      if (error) throw error;
      const best = new Map<number, { memberId: number; score: number; time: number | null }>();
      const better = (a: { score: number; time: number | null }, b: { score: number; time: number | null }) =>
        goal === "score" ? a.score > b.score : (a.time ?? Infinity) < (b.time ?? Infinity);
      for (const r of data) {
        const run = { memberId: r.member_id, score: r.score, time: r.race_time };
        const was = best.get(r.member_id);
        if (!was || better(run, was)) best.set(r.member_id, run);
      }
      return Array.from(best.values()).sort((a, b) => (better(a, b) ? -1 : better(b, a) ? 1 : 0));
    },
    retry: false,
  });
